<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import { formatDateTime, formatDuration, formatNumber } from '../helpers';

const props = defineProps({
  preview: { type: Object, default: null },
  isLoading: { type: Boolean, default: false },
  empty: { type: Boolean, default: false },
  timezone: { type: String, default: '' },
});
const emit = defineEmits(['refresh']);
const { t, locale } = useI18n();
const num = v => formatNumber(v, locale.value);

const rows = computed(() => {
  const p = props.preview || {};
  return [
    { key: 'FOUND', value: p.total, strong: true },
    { key: 'MISSING_PHONE', value: p.missing_phone, negative: true },
    { key: 'INVALID_PHONE', value: p.invalid_phone, negative: true },
    { key: 'DUPLICATE', value: p.duplicate, negative: true },
    { key: 'BLACKLISTED', value: p.blacklisted, negative: true },
  ];
});
</script>

<template>
  <div
    class="flex flex-col gap-3 p-4 rounded-xl outline outline-1 outline-n-weak bg-n-solid-1"
  >
    <div class="flex items-center justify-between">
      <h4 class="text-sm font-medium text-n-slate-12">
        {{ t('WASAPI_CAMPAIGNS.AUDIENCE.PREVIEW_TITLE') }}
      </h4>
      <Button
        icon="i-lucide-refresh-cw"
        variant="ghost"
        color="slate"
        size="xs"
        :is-loading="isLoading"
        :disabled="empty"
        @click="emit('refresh')"
      />
    </div>
    <div v-if="isLoading && !preview" class="flex justify-center py-4">
      <Spinner />
    </div>
    <p v-else-if="empty || !preview" class="text-sm text-n-slate-11">
      {{ t('WASAPI_CAMPAIGNS.AUDIENCE.EMPTY_HINT') }}
    </p>
    <template v-else-if="preview">
      <dl class="flex flex-col text-sm">
        <div
          v-for="row in rows"
          :key="row.key"
          class="flex items-center justify-between py-1"
          :class="
            row.negative && !row.value ? 'text-n-slate-10' : 'text-n-slate-12'
          "
        >
          <dt :class="row.strong ? 'font-medium' : ''">
            {{ t(`WASAPI_CAMPAIGNS.AUDIENCE.${row.key}`) }}
          </dt>
          <dd
            class="tabular-nums"
            :class="row.negative && row.value ? 'text-n-amber-11' : ''"
          >
            {{
              row.negative && row.value ? `−${num(row.value)}` : num(row.value)
            }}
          </dd>
        </div>
        <div
          class="flex items-center justify-between py-2 mt-1 border-t border-n-weak font-semibold text-n-slate-12"
        >
          <dt>{{ t('WASAPI_CAMPAIGNS.AUDIENCE.ELIGIBLE') }}</dt>
          <dd class="text-lg tabular-nums">{{ num(preview.eligible) }}</dd>
        </div>
      </dl>
      <div
        v-if="preview.estimate"
        class="flex flex-col gap-1 pt-2 text-xs border-t border-n-weak text-n-slate-11"
      >
        <span>
          {{ t('WASAPI_CAMPAIGNS.AUDIENCE.ESTIMATE') }}:
          <strong class="text-n-slate-12">{{
            formatDuration(preview.estimate.duration_minutes, t)
          }}</strong>
        </span>
        <span v-if="preview.eligible">
          {{ t('WASAPI_CAMPAIGNS.AUDIENCE.COMPLETION') }}:
          <strong class="text-n-slate-12">{{
            formatDateTime(
              preview.estimate.completion_at,
              locale,
              timezone || undefined
            )
          }}</strong>
          <span v-if="timezone"> ({{ timezone }})</span>
        </span>
      </div>
      <div
        v-if="preview.sample?.length"
        class="flex flex-col gap-1 pt-2 text-xs border-t border-n-weak"
      >
        <span class="text-n-slate-11">{{
          t('WASAPI_CAMPAIGNS.AUDIENCE.SAMPLE')
        }}</span>
        <span
          v-for="c in preview.sample"
          :key="c.id"
          class="text-n-slate-12 truncate"
        >
          {{ c.name }} · {{ c.phone_number }}
        </span>
      </div>
    </template>
  </div>
</template>
